import { useCallback, useEffect, useRef } from "react";

export const useRecaptcha = () => {
  const scriptLoadedRef = useRef(false);

  const loadRecaptchaScript = useCallback(() => {
    // Evitar cargar múltiples veces
    if (window.grecaptcha || scriptLoadedRef.current) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = `https://www.google.com/recaptcha/api.js?render=${process.env.REACT_APP_RECAPTCHA_SITE_KEY}`;
      script.async = true;

      script.onload = () => {
        scriptLoadedRef.current = true;
        resolve();
      };

      script.onerror = () => {
        reject(new Error("Error cargando reCAPTCHA"));
      };

      document.head.appendChild(script);
    });
  }, []);

  const executeRecaptcha = useCallback(
    async (action) => {
      if (!window.grecaptcha) {
        await loadRecaptchaScript();
      }

      return new Promise((resolve, reject) => {
        if (!window.grecaptcha) {
          reject(new Error("reCAPTCHA no está cargado"));
          return;
        }

        window.grecaptcha.ready(() => {
          window.grecaptcha
            .execute(process.env.REACT_APP_RECAPTCHA_SITE_KEY, { action })
            .then(resolve)
            .catch(reject);
        });
      });
    },
    [loadRecaptchaScript]
  );

  // Función para verificar si reCAPTCHA está disponible y ejecutarlo
  const executeAndValidateRecaptcha = useCallback(
    async (action) => {
      try {
        // Verificar si reCAPTCHA está cargado
        if (!window.grecaptcha && process.env.NODE_ENV !== "development") {
          await loadRecaptchaScript();

          // Dar tiempo para que se inicialice
          await new Promise((resolve) => setTimeout(resolve, 1000));

          if (!window.grecaptcha) {
            throw new Error("reCAPTCHA no se pudo cargar correctamente");
          }
        }

        const token = await executeRecaptcha(action);

        // Verificar que el token sea válido (no vacío o null)
        if (!token || token.length < 10) {
          throw new Error("Token de reCAPTCHA inválido");
        }

        return { success: true, token };
      } catch (error) {
        console.error("Error en reCAPTCHA:", error);
        return { success: false, token: null, error: error.message };
      }
    },
    [executeRecaptcha, loadRecaptchaScript]
  );

  const cleanupRecaptcha = useCallback(() => {
    const recaptchaBadge = document.querySelector(".grecaptcha-badge");

    if (recaptchaBadge) {
      recaptchaBadge.style.display = "none";
    }

    const recaptchaElements = document.querySelectorAll('[style*="recaptcha"]');
    recaptchaElements.forEach((element) => {
      element.style.display = "none";
    });
  }, []);

  return {
    executeRecaptcha,
    executeAndValidateRecaptcha,
    loadRecaptchaScript,
    cleanupRecaptcha,
  };
};
